import { useEffect, useCallback, useState } from 'react';
import packageJson from '../../package.json';

const CURRENT_VERSION = packageJson.version;
const VERSION_CHECK_KEY = 'app_version';
const CHECK_INTERVAL = 5 * 60 * 1000; // Vérifier toutes les 5 minutes
const UPDATE_IN_PROGRESS_KEY = 'update_in_progress';

/**
 * Hook pour vérifier automatiquement les mises à jour de l'application
 * Compare la version locale avec meta.json du serveur
 * Force le rechargement si une nouvelle version est disponible
 */
const useVersionCheck = () => {
  const [hasCheckedOnMount, setHasCheckedOnMount] = useState(false);
  const clearCacheAndReload = useCallback(async (serverVersion) => {
    console.log('🔄 Nouvelle version détectée, nettoyage du cache...');
    
    try {
      // Marquer qu'une mise à jour est en cours
      localStorage.setItem(UPDATE_IN_PROGRESS_KEY, 'true');
      
      // 1. Supprimer tous les caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Caches supprimés');
      }

      // 2. Désinscrire le service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('✅ Service Worker désinscrit');
      }

      // 3. Mémoriser la version SERVEUR (et non celle du bundle courant) :
      //    sinon, si le navigateur resert un ancien bundle depuis son cache HTTP,
      //    la comparaison échoue à nouveau au prochain passage et on reboucle
      //    indéfiniment sur un rechargement.
      //
      //    ⚠️ On ne vide surtout PAS le localStorage : cela supprimait le token
      //    JWT et le state redux-persist, donc déconnectait le parent à chaque
      //    mise à jour ("Impossible de charger le profil de votre enfant").
      //    Le nettoyage des caches + la désinscription du SW suffisent à servir
      //    la nouvelle version.
      localStorage.setItem(VERSION_CHECK_KEY, serverVersion ?? CURRENT_VERSION);
      localStorage.setItem(UPDATE_IN_PROGRESS_KEY, 'true');
      
      // 4. Recharger la page courante en contournant le cache HTTP de index.html.
      //    On conserve les query params existants (utm_source, etc. : ils sont
      //    exploités par useTrackUtmVisit).
      const search = new URLSearchParams(window.location.search);
      search.set('v', serverVersion ?? CURRENT_VERSION);
      window.location.replace(
        `${window.location.pathname}?${search.toString()}${window.location.hash}`
      );
    } catch (error) {
      console.error('❌ Erreur lors du nettoyage du cache:', error);
      // Forcer le rechargement quand même (sans perdre la session)
      window.location.reload();
    }
  }, []);

  const checkVersion = useCallback(async () => {
    try {
      // Ne pas vérifier en mode développement (localhost) sauf si REACT_APP_TEST_VERSION_CHECK est activé
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const forceTestInDev = process.env.REACT_APP_TEST_VERSION_CHECK === 'true';
      
      if (isLocalhost && !forceTestInDev) {
        console.log('🔧 Mode développement - vérification de version désactivée');
        console.log('💡 Pour tester, ajoutez REACT_APP_TEST_VERSION_CHECK=true dans .env.development');
        return;
      }

      // Si une mise à jour est déjà en cours, ne pas vérifier à nouveau
      const updateInProgress = localStorage.getItem(UPDATE_IN_PROGRESS_KEY);
      if (updateInProgress === 'true') {
        console.log('⏳ Mise à jour en cours, nettoyage du flag...');
        localStorage.removeItem(UPDATE_IN_PROGRESS_KEY);
        return;
      }

      const now = Date.now();

      // Récupérer la version du serveur (avec cache-busting)
      const response = await fetch(`/meta.json?t=${now}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('⚠️ Impossible de vérifier la version (meta.json non trouvé)');
        return;
      }

      const meta = await response.json();
      const serverVersion = meta.version;
      const storedVersion = localStorage.getItem(VERSION_CHECK_KEY);

      console.log(`📱 Version locale: ${CURRENT_VERSION}`);
      console.log(`🌐 Version serveur: ${serverVersion}`);
      console.log(`💾 Version stockée: ${storedVersion}`);

      // Première visite : il n'y a encore ni cache ni service worker à
      // invalider. On enregistre simplement la version pour éviter un
      // rechargement inutile (qui ferait perdre les paramètres de campagne).
      if (!storedVersion) {
        localStorage.setItem(VERSION_CHECK_KEY, serverVersion);
        console.log('✅ Première visite, version enregistrée');
        return;
      }

      // Si la version serveur est différente de la version stockée
      if (serverVersion !== storedVersion) {
        console.log('🆕 Nouvelle version disponible! Rechargement automatique...');
        
        // Recharger IMMÉDIATEMENT (la session est conservée)
        await clearCacheAndReload(serverVersion);
      } else {
        console.log('✅ Application à jour');
      }
    } catch (error) {
      console.error('❌ Erreur vérification version:', error);
      // Ne rien faire en cas d'erreur (probablement en développement)
    }
  }, [clearCacheAndReload]);

  useEffect(() => {
    // Vérifier au chargement initial (une seule fois)
    if (!hasCheckedOnMount) {
      checkVersion(true);
      setHasCheckedOnMount(true);
    }

    // Vérifier périodiquement (toutes les 5 minutes)
    const interval = setInterval(() => checkVersion(false), CHECK_INTERVAL);

    // Vérifier quand l'app revient au premier plan
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Vérifier quand l'utilisateur revient en ligne
    const handleOnline = () => {
      checkVersion(false);
    };
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkVersion, hasCheckedOnMount]);

  return { checkVersion, clearCacheAndReload };
};

export default useVersionCheck;
